/**
 * Document-related utility functions
 * Extracted from App.js for better organization and reusability
 */

import { formatDateForFilename } from './dateHelpers';
import { generateDownloadFilename } from './apiHelpers';

/**
 * Generate document filename based on document metadata and form type
 * Uses template name when available, falls back to form type
 * Uses document-specific dates based on form type
 * @param {Object} doc - Document object
 * @param {Object} client - Client object
 * @returns {string} - Generated filename with appropriate extension
 */
export const generateDocumentDownloadFilename = (doc, client) => {
  // Prefer the document's own name (title/template/name, or the uploaded
  // filename without its extension) over the generic form-type category.
  const filenameStem = doc.filename ? doc.filename.replace(/\.[^.]+$/, '') : '';
  const templateName = doc.title || doc.template_name || doc.name || filenameStem;
  const prettify = (s) =>
    String(s || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  const formType = prettify(doc.form_type || doc.document_type || 'Document');
  const docName = templateName || formType;
  
  const docDate = doc.date_of_service || doc.created_at || doc.uploaded_at;
  const dateStr = docDate ? formatDateForFilename(new Date(docDate)) : formatDateForFilename(new Date());
  
  // Get file extension from original filename or default to pdf
  const ext = doc.filename?.split('.').pop() || 'pdf';
  
  // Determine how to format the name based on document type
  let docTypeName = docName;
  const docNameLC = docName.toLowerCase();
  const formTypeLC = formType.toLowerCase();
  
  // For Progress Notes, use "for DOS" format
  if (formTypeLC.includes('progress note') || docNameLC.includes('progress note')) {
    docTypeName = `${docName} for DOS ${dateStr}`;
  } 
  // For Consents/Policies, use "Signed" format
  else if (formTypeLC.includes('consent') || formTypeLC.includes('polic')) {
    docTypeName = `${docName} Signed ${dateStr}`;
  }
  // For Treatment Plans, use "Dated" format
  else if (formTypeLC.includes('treatment plan') || docNameLC.includes('treatment plan')) {
    docTypeName = `${docName} Dated ${dateStr}`;
  }
  // For everything else, just append the date
  else {
    docTypeName = `${docName} ${dateStr}`;
  }
  
  return generateDownloadFilename(client, docTypeName, ext);
};
